import React, { useEffect, useMemo, useState } from 'react';
import '../assets/css/AstroDetailsForm.css';
import { useSelector, useDispatch } from 'react-redux';
import axios from 'axios';
import Navbar from '../components/Navbar';
import { jwtDecode } from 'jwt-decode';
import api from '../api/axios';
import { updateDetails, updateVisitedUserDetails } from '../redux/detailsSlice';
import { useLocation, useNavigate } from 'react-router-dom';
import posthog from 'posthog-js';
import { BackgroundDiv } from '../components/StyledComponents';
import BirthDetailsForm from '../components/BirthDetailsForm';
import { styled } from '@mui/material/styles';
import { useAuth } from '../context/AuthContext';

const AstroDetailsForm = () => {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [userId, setUserId] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [showData, setShowData] = useState(false);
  const [formData, setFormData] = useState({
    dob: null,
    timeOfBirth: null,
    placeOfBirth: '',
  });

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const { logout } = useAuth();

  const handleLogout = () => {
    posthog.reset();
    logout();
    dispatch(updateVisitedUserDetails(false));
    navigate('/login', { replace: true });
  };

  useEffect(() => {
    dispatch(updateVisitedUserDetails(true));

    const params = new URLSearchParams(location.search);
    const redirect = params.get('redirect');
    console.log('oath test', params, redirect);

    if (redirect) {
      navigate(redirect ?? '/detail', { replace: true });
    }
  }, [location, navigate]);

  useEffect(() => {
    setTimeout(() => {
      setShowData(true);
    }, 2000);
  }, []);

  const { isLogin, login } = useAuth();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    // It is important to first check URL for token.
    // Token will be in URL if the user signed up from Google OAuth
    let token = params.get('token') || localStorage.getItem('token');

    if (token) {
      localStorage.setItem('token', token);
      const decoded = jwtDecode(token);
      const id = decoded.email;
      setUserId(id);

      axios
        .get(`${api}/user/details/${id}`)
        .then((res) => {
          setEmail(res.data[0].email);
          setName(res.data[0].name);
          posthog.identify(res.data[0].email, { name: res.data[0].name });
          // If user already filled details, skip form
          if (res.data[0].placeOfBirth) {
            localStorage.setItem('islogin', 'true');
            login();
            dispatch(
              updateDetails({
                name: res.data[0].name,
                placeOfBirth: res.data[0].placeOfBirth,
                timeOfBirth: res.data[0].timeOfBirth,
                dob: res.data[0].dob,
              })
            );
            navigate('/welcome', { replace: true });
          }
        })
        .catch((err) => {
          console.error(err);
          navigate('/login', { replace: true });
          logout();
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [isLogin]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const submitEnabled = useMemo(() => {
    return (
      email != null &&
      email.length > 1 &&
      name != null &&
      name.length > 1 &&
      formData.dob != null &&
      formData.placeOfBirth != null &&
      formData.timeOfBirth != null
    );
  }, [email, name, formData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!submitEnabled) {
      setError('Please fill all the details before proceeding.');
      return;
    }

    try {
      // Include name in the update so it gets saved if not already set
      await axios.put(`${api}/user/details/${userId}`, { ...formData, name });
      localStorage.setItem('islogin', 'true');
      login();
      dispatch(
        updateDetails({
          name: name,
          ...formData,
        })
      );
      posthog.capture('birth_details_submitted', {
        place_of_birth: formData.placeOfBirth,
      });
      navigate('/welcome');
    } catch (err) {
      const msg = err.response?.data?.msg || 'Signup failed. Please try again.';
      setError(msg);
      posthog.captureException(err, {
        extra: { context: 'birth_details_submitted' },
      });
      console.error('Error saving details:', err);
    }
  };

  if (loading) return null;

  return (
    showData && (
      <>
        <BackgroundDiv>
          <Navbar text={email.charAt(0).toUpperCase()} />
          <StyledContainer>
            <StyledHeading>
              We need some Details <span className="emoji">😊</span>
            </StyledHeading>
            <StyledParagraph>
              Accurate <span className="highlight">Kundli</span>, Real
              predictions, all we need is your Birth details
            </StyledParagraph>
            <BirthDetailsForm
              name={name}
              setName={setName}
              handleSubmit={handleSubmit}
              formData={formData}
              setFormData={setFormData}
            />
            {error && <div className="error-message">{error}</div>}
          </StyledContainer>
        </BackgroundDiv>
      </>
    )
  );
};

const StyledContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  justifyContent: 'center',
  marginLeft: '152px',
  marginTop: '60px',
  [theme.breakpoints.down('md')]: {
    marginLeft: '16px',
  },
}));

const StyledHeading = styled('div')(({ theme }) => ({
  marginBottom: '12px',
  fontSize: '50px',
  fontWeight: 500,
  color: theme.palette.text.primary,
}));
const StyledParagraph = styled('p')(({ theme }) => ({
  fontSize: '24px',
  fontWeight: 400,
  color: theme.palette.mode === 'dark' ? '#ffffffB3' : '#000000B3',
  marginBottom: '80px',
}));

export default AstroDetailsForm;
